// THE CLIENT'S PHYSICIAN (2026-09-25).
//
// Ana Ruiz at Squirtin' Ink (Florida, tattoo and piercing), an hour after activating for the year: "For the
// piercing forms can we add a physician name , number and address." No state's rule names a physician on the
// client's record (the 51 compliance records carry none), so this is the shop's own switch, and it is ONE PER
// SERVICE, because she asked for it on the piercing form and not the tattoo one, and a shop that does both is
// two forms at one counter: `public_data.ask_physician_piercing` and `public_data.ask_physician_tattoo`, each
// off until the owner turns it on in Settings → Waiver form → Physician.
//
// THE THREE BOXES ARE OPTIONAL. A walk-in with no doctor still signs (Chris, 2026-09-10: "We should never have
// it so if someone signs and fills out everything they can't finish"): nothing here gates the submit, and the
// signed waiver prints "Physician: (None given)" for a client who left them blank, so the shop that asked can
// see the question was put. The API reads the same two keys the same way (common.ask_physician_of) and prints
// only when the switch for the service signed is on, so what is asked is what prints.

import type { ServiceType } from "./service";
import { settingForcedBy } from "./stateForms";

/** The three keys the form posts, ONLY when it asked; Form.tsx strips them otherwise so every other waiver's
 *  body is the one it has always been. The API's PHYSICIAN_FIELDS are these three, byte for byte. */
export const PHYSICIAN_FIELDS = ["physicianName", "physicianPhone", "physicianAddress"] as const;

/** The switch's key for a service, or null for a service that has none. A COSMETIC TATTOO HAS NONE YET (TW-0279): its
 *  own switch, `ask_physician_cosmetic`, is a later cut of docs/COSMETIC.md, and the API prints no physician lines on a
 *  cosmetic record (common.PHYSICIAN_SETTINGS has no cosmetic key), so a cosmetic visit is asked nothing here. Never the
 *  tattoo's key by fallback: a shop that asks on its tattoos has not said it asks on its permanent makeup. */
export const physicianSettingFor = (service: ServiceType): "ask_physician_tattoo" | "ask_physician_piercing" | null =>
  service === "piercing" ? "ask_physician_piercing" : service === "tattoo" ? "ask_physician_tattoo" : null;

/** Does this shop ask a client who is here for `service` who their doctor is? Only a literal true on the
 *  service's own key counts, the way every switch since the price is read; nothing before a `both` shop's client
 *  has picked, because there is no form to ask it on yet, and nothing for a service with no switch. */
export function asksPhysician(
  publicData: { ask_physician_tattoo?: unknown; ask_physician_piercing?: unknown } | null | undefined,
  service: ServiceType | "",
): boolean {
  if (!service) return false;
  const setting = physicianSettingFor(service);
  return setting !== null && publicData?.[setting] === true;
}

/** TW-0629 (2026-11-04) — DOES THIS SHOP'S STATE'S RULE PUT THE PHYSICIAN ON A RECORD OF `service`? Florida's body-piercing customer
 *  record holds "Their physician's name, address and telephone number;" (Fla. Admin. Code R. 64E-19.007(3)(c)), so its record names
 *  `physician_piercing`, the piercing's own switch forced on (scripts/state-forms.py VOCAB), and a Florida piercing is asked whatever
 *  the shop's switch says; the three boxes stay optional. Nothing before a both shop's client has picked, and nothing for a service
 *  with no switch. The API's twin is common.physician_asked_on. */
export function physicianForcedBy(state: string | null | undefined, service: ServiceType | "", day?: string): boolean {
  if (!service) return false;
  const setting = physicianSettingFor(service);
  return setting !== null && settingForcedBy(state, setting, day, service);
}
